import { useEffect, useRef, useCallback } from 'react';
import { subscribeConnectionHealth } from '../services/connectionHealth';

// Owner ask, 2026-07-28: auto-retry a failed tree branch "on health recovery (2x?) +
// delay". Two consecutive good pings, because a single one can be the good half of a
// flapping connection; the delay then gives a cold-starting backend a moment to finish
// waking rather than spending the retry on a request that was always going to fail.
export const RECOVERY_MIN_OK_PINGS = 2;
export const RECOVERY_RETRY_DELAY_MS = 1500;

// #750: caps how many times a SPECIFIC branch gets auto-retried before it is left for a
// human. Without a cap, a branch that is durably broken (not just briefly unreachable —
// a real 500, a deleted parent) would be re-attempted on every subsequent healthy
// heartbeat for as long as it kept failing: individually each check is reasonable
// ("connected, streak≥2, branch still failed → retry"), but strung together they are an
// auto-retry storm wearing three reasonable clauses. This is the bound that bug asked
// for. It is per-id and resets on success (loadChildren clears the id from
// `failedChildIds` the moment a retry — auto or manual — lands) or on a user-initiated
// click of Retry (see `manualRetryChildren` below), so a human always gets a full fresh
// budget of auto-retries after they've looked at it.
export const RECOVERY_MAX_AUTO_ATTEMPTS = 3;

/**
 * Auto-retries tree branches sitting in `failedChildIds` once the backend has answered
 * `RECOVERY_MIN_OK_PINGS` heartbeats in a row — **state-based**, not edge-triggered.
 *
 * #750: the previous version of this hook (inlined in RequirementsTreeView) only armed
 * when the connection health service OBSERVED an outage — a ping actually failing. The
 * healthy heartbeat cadence is 15s (`pollSchedule.heartbeat.intervalMs`), so a backend
 * blip shorter than the gap between two pings could fail a branch's own `/children`
 * fetch (the user sees "Failed to load") and fully recover before the next health ping
 * — the health service never saw anything go wrong, `sawOutage` never flipped on, and
 * the auto-retry never armed. The branch sat "Failed to load" until the user clicked
 * Retry, exactly the case this mechanism exists for.
 *
 * The fix: on EVERY healthy heartbeat, check state directly — is anything in
 * `failedChildIds` right now, and has the connection held for the required streak? — and
 * retry it if so, regardless of whether THIS PARTICULAR failure was ever observed by a
 * health ping. `subscribeConnectionHealth` already emits on every successful ping (not
 * just on the transition into 'connected' — see connectionHealth.js), so this needs no
 * extra polling of its own; it just has to stop gating on the edge.
 *
 * Retrying on every qualifying heartbeat forever would itself be a retry storm for a
 * branch that is durably broken rather than briefly unreachable — `RECOVERY_MAX_AUTO_ATTEMPTS`
 * bounds that (see its comment above). That replaces the old `firedForThisRecovery`
 * edge-latch, which is no longer meaningful once there is no edge to latch on.
 */
export function useFailedBranchRecovery({ failedChildIds, retryChildren }) {
  // Refs so the health subscription (mounted once) always sees the latest state/callback
  // without needing to re-subscribe — `failedChildIds` and `retryChildren` change on
  // every tree load.
  const failedChildIdsRef = useRef(failedChildIds);
  const retryChildrenRef = useRef(retryChildren);
  useEffect(() => { failedChildIdsRef.current = failedChildIds; }, [failedChildIds]);
  useEffect(() => { retryChildrenRef.current = retryChildren; }, [retryChildren]);

  // id -> number of automatic retries already spent on it this "episode". Lives for the
  // life of the component, not per-effect-run, so it survives the health subscription
  // being torn down/recreated (it never is, in practice — deps are `[]`) and so
  // `manualRetryChildren` can reach in and clear an entry from outside this effect.
  const autoAttemptsRef = useRef(new Map());

  useEffect(() => {
    let timer = null;
    const cancel = subscribeConnectionHealth(({ status, consecutiveOk }) => {
      if (status !== 'connected') {
        // An observed outage mid-flight: don't fire into it. (No need to touch the
        // attempt counters here — they are per-id and only matter once a branch is
        // actually retried; leaving them as-is is what lets a still-failed branch pick
        // up where it left off once the connection holds again.)
        if (timer) { clearTimeout(timer); timer = null; }
        return;
      }
      if (consecutiveOk < RECOVERY_MIN_OK_PINGS) return;
      if (timer) return; // a retry is already scheduled for this healthy window
      const failed = failedChildIdsRef.current;
      if (!failed || !failed.size || !retryChildrenRef.current) return;
      const attempts = autoAttemptsRef.current;
      // Nothing to do if every currently-failed branch has already spent its budget —
      // this is what stops the loop once a branch is durably broken rather than
      // briefly unreachable.
      const due = [...failed].some((id) => (attempts.get(id) || 0) < RECOVERY_MAX_AUTO_ATTEMPTS);
      if (!due) return;
      timer = setTimeout(() => {
        timer = null;
        // Re-read at fire time, not schedule time: the failed set (and the connection)
        // may have moved on during the delay.
        const stillFailed = failedChildIdsRef.current;
        if (!stillFailed || !stillFailed.size || !retryChildrenRef.current) return;
        // Snapshot: `retryChildren` (via loadChildren) mutates the very set being
        // iterated — it clears an id from `failedChildIds` as soon as its retry starts.
        [...stillFailed].forEach((id) => {
          const count = attempts.get(id) || 0;
          if (count >= RECOVERY_MAX_AUTO_ATTEMPTS) return; // capped — see comment above
          attempts.set(id, count + 1);
          retryChildrenRef.current(id);
        });
      }, RECOVERY_RETRY_DELAY_MS);
    });
    return () => { cancel(); if (timer) clearTimeout(timer); };
  }, []);

  // The Retry button's handler: identical to `retryChildren` except it first clears the
  // id's auto-attempt count, so a human who has looked at a stubborn branch and asked
  // for another go always gets a full fresh budget of subsequent auto-retries — the cap
  // above is there to stop an unattended loop, not to outlast a manual intervention.
  const manualRetryChildren = useCallback((nodeOrId) => {
    const id = typeof nodeOrId === 'string' ? nodeOrId : nodeOrId?.id;
    if (id) autoAttemptsRef.current.delete(id);
    return retryChildrenRef.current ? retryChildrenRef.current(nodeOrId) : undefined;
  }, []);

  return { manualRetryChildren };
}

export default useFailedBranchRecovery;
